import FleetPlanningAPI, {
  type SiteConfigPayload,
} from 'api/fleet-planning.api';
import { BaseHandler } from './base.handler';
import {
  setDispatchSummaryPlan,
  setLoading,
  setPlans,
} from 'store/fleet-planning.slice';
import {
  hydrateSiteDataFromAPI,
  loadSiteConfigFromAPI,
  setMinimalFleetCount,
} from 'store/site-config.slice';

import {
  type NotificationState,
  showNotification,
} from 'store/notification.slice';
import {
  computePlanMetrics,
  formatTripsStatsForRedux,
} from 'views/FleetPlanner/utils/dataFormatters';
import {
  type LoadingDumpingDurationsResponseType,
  type TravelStatsResponseType,
  type DispatchPlanFuelUsageMetricsResponseType,
  type DispatchPlanMaterialDumpResponseType,
  type DispatchPlanTravelTimeMetricsResponseType,
  type DispatchPlanTripsResponseType,
  type PlanDataType,
} from 'views/FleetPlanner/CreateDispatchPlan/Steps/types/steps.types';
import {
  type DispatchPlanSummaryPlanType,
  type DispatchPlanHistoryResponseItemType,
  type DispatchPlanSummaryType,
} from 'views/FleetPlanner/DispatchSummary/types/dispatch-summary.types';
import { type DispatchPlanStatusType } from 'views/FleetPlanner/types/fleet-planner.types';

/**
 * Normalise a timestamp that may arrive as a time-only string ("HH:MM" /
 * "HH:MM:SS"), a full ISO string, or a Unix number.  When a base date is
 * supplied, time-only values are anchored to that date.  A simple day-rollover
 * heuristic bumps the date by +1 when a timestamp is more than 12 h earlier
 * than the previous one (handles plans that run past midnight).
 */
function normalizeTs(
  raw: string | number | null | undefined,
  baseDateStr: string,
  prevMs?: number
): string {
  if (raw == null) return '';
  const s = String(raw).trim();

  let iso: string;

  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    // Already a full date string
    iso = s;
  } else if (/^\d{2}:\d{2}(:\d{2})?$/.test(s)) {
    // Time-only: combine with base date
    const time = s.length === 5 ? `${s}:00` : s;
    iso = `${baseDateStr}T${time}Z`;
  } else if (/^\d+$/.test(s)) {
    // Numeric string – treat as Unix timestamp
    const n = Number(s);
    iso = new Date(n > 1e10 ? n : n * 1000).toISOString();
  } else {
    iso = s;
  }

  // Day-rollover: if this timestamp is >12 h before the previous one,
  // shift it forward by 1 day.
  if (prevMs !== undefined && prevMs > 0) {
    const ms = new Date(iso).getTime();
    if (!isNaN(ms) && prevMs - ms > 12 * 60 * 60 * 1000) {
      const shifted = new Date(ms + 24 * 60 * 60 * 1000);
      iso = shifted.toISOString();
    }
  }

  return iso;
}

function parsePlanRow(
  row: any[],
  baseDateStr: string,
  prevMs?: number
): PlanDataType {
  const startTs = normalizeTs(row[0], baseDateStr, prevMs);
  const endTs = normalizeTs(row[1], baseDateStr, new Date(startTs).getTime());
  return {
    start_ts: startTs,
    end_ts: endTs,
    start_location: row[2] as string,
    end_location: row[3] as string,
    intermediate_location: (row[4] as string | null) ?? null,
    action: ((row[5] as string) ?? '').toLowerCase(),
    device: row[6] as string,
    bumper_number: row[6] as string,
    duration: row[7] as number,
    truck_id: (row[8] as string | null) ?? null,
    loading_area: (row[9] as string | null) ?? null,
    dumping_area: (row[10] as string | null) ?? null,
    travel_time: row[11] as number,
    fuel_usage: row[12] as number,
    material_dumped: row[13] as number,
  };
}

function serializePlanRow(record: PlanDataType): any[] {
  return [
    record.start_ts, // 0
    record.end_ts, // 1
    record.start_location, // 2
    record.end_location, // 3
    record.intermediate_location ?? null, // 4
    record.action, // 5
    record.device, // 6
    record.duration ?? null, // 7
    record.truck_id ?? null, // 8
    record.loading_area ?? null, // 9
    record.dumping_area ?? null, // 10
    record.travel_time, // 11
    record.fuel_usage, // 12
    record.material_dumped, // 13
  ];
}

function parsePlanData(raw: any[], baseDateStr = ''): PlanDataType[] {
  if (!Array.isArray(raw) || raw.length === 0) return [];
  if (Array.isArray(raw[0])) {
    let prevMs = 0;
    return raw.map((row) => {
      const record = parsePlanRow(row, baseDateStr, prevMs);
      prevMs = new Date(record.start_ts).getTime() || prevMs;
      return record;
    });
  }
  return raw as PlanDataType[];
}

export default class FleetPlanningHandler extends BaseHandler {
  private readonly api: FleetPlanningAPI;

  constructor() {
    super();
    this.api = new FleetPlanningAPI();
  }

  async createDispatchPlan(payload: Record<string, any>): Promise<{
    userPlan: {
      planData: PlanDataType[];
      metrics: {
        travel_time: DispatchPlanTravelTimeMetricsResponseType[];
        fuel_usage: DispatchPlanFuelUsageMetricsResponseType[];
        material_dumped: DispatchPlanMaterialDumpResponseType[];
        trips: DispatchPlanTripsResponseType[];
      };
    };
    optimizedPlan: {
      planData: PlanDataType[];
      metrics: {
        travel_time: DispatchPlanTravelTimeMetricsResponseType[];
        fuel_usage: DispatchPlanFuelUsageMetricsResponseType[];
        material_dumped: DispatchPlanMaterialDumpResponseType[];
        trips: DispatchPlanTripsResponseType[];
      };
    };
    mlInputPayload: Record<string, any>;
  }> {
    try {
      this.dispatch(setLoading(true));
      const response = await this.api.createDispatchPlanV2(payload);
      const { userPlan, optimizedPlan, mlInputPayload } = response ?? {};
      if (
        Array.isArray(userPlan?.planData) &&
        userPlan.planData.length > 0 &&
        Array.isArray(optimizedPlan?.planData) &&
        optimizedPlan.planData.length > 0
      ) {
        this.dispatch(
          setPlans({
            userPlan,
            optimizedPlan,
            mlInputPayload,
          })
        );
        const notificationPayload: NotificationState = {
          isLoading: false,
          message: 'Plans created successfully!',
          show: true,
          type: 'success',
        };
        this.dispatch(showNotification(notificationPayload));
        return response;
      } else {
        throw new Error('Empty plans returned');
      }
    } catch (_) {
      this.handleError('An error occurred while creating plans.');
      const notificationPayload: NotificationState = {
        isLoading: false,
        message: 'Error while creating plans, please try again!',
        show: true,
        type: 'error',
      };
      this.dispatch(showNotification(notificationPayload));
      throw new Error('An error occurred while creating plans.');
    } finally {
      this.dispatch(setLoading(false));
    }
  }

  async createDispatchPlanV3(payload: Record<string, any>): Promise<void> {
    try {
      this.dispatch(setLoading(true));
      const response = await this.api.createDispatchPlanV3(payload);
      const { userPlan, optimizedPlan, mlInputPayload } = response ?? {};
      // Extract YYYY-MM-DD from the first truck's startTime so that
      // time-only timestamps ("HH:MM") can be anchored to the correct date.
      const rawStartTime: string =
        payload?.trucks?.[0]?.startTime ?? payload?.startDateTime ?? '';
      const baseDateStr = rawStartTime.slice(0, 10); // "YYYY-MM-DD"

      const parsedUserPlanData = userPlan
        ? parsePlanData(userPlan.planData, baseDateStr)
        : [];
      const parsedOptimizedPlanData = optimizedPlan
        ? parsePlanData(optimizedPlan.planData, baseDateStr)
        : [];
      if (parsedUserPlanData.length > 0 && parsedOptimizedPlanData.length > 0) {
        const parsedUserPlan = {
          ...userPlan,
          planData: parsedUserPlanData,
          metrics: computePlanMetrics(parsedUserPlanData),
        };
        const parsedOptimizedPlan = {
          ...optimizedPlan,
          planData: parsedOptimizedPlanData,
          metrics: computePlanMetrics(parsedOptimizedPlanData),
        };
        this.dispatch(
          setPlans({
            userPlan: parsedUserPlan,
            optimizedPlan: parsedOptimizedPlan,
            mlInputPayload,
          })
        );
        const notificationPayload: NotificationState = {
          isLoading: false,
          message: 'Plans created successfully!',
          show: true,
          type: 'success',
        };
        this.dispatch(showNotification(notificationPayload));
      } else {
        throw new Error('Empty plans returned');
      }
    } catch (_) {
      this.handleError('An error occurred while creating plans.');
      const notificationPayload: NotificationState = {
        isLoading: false,
        message: 'Error while creating plans, please try again!',
        show: true,
        type: 'error',
      };
      this.dispatch(showNotification(notificationPayload));
      throw new Error('An error occurred while creating plans.');
    } finally {
      this.dispatch(setLoading(false));
    }
  }

  async getTripsStats(payload?: { aggregateByDevice?: boolean }): Promise<{
    travelStats: TravelStatsResponseType;
    loadingDumpingDurations: LoadingDumpingDurationsResponseType;
  }> {
    try {
      const response: {
        travelStats: TravelStatsResponseType;
        loadingDumpingDurations: LoadingDumpingDurationsResponseType;
      } = await this.api.getTripsStats(payload);
      const formattedData = formatTripsStatsForRedux(
        response.travelStats,
        response.loadingDumpingDurations
      );
      this.dispatch(hydrateSiteDataFromAPI(formattedData));
      return response;
    } catch (_) {
      this.handleError('Error while fetching travel and activity mins!');
      const notificationPayload: NotificationState = {
        isLoading: false,
        message: 'Error while fetching travel and activity mins!',
        show: true,
        type: 'error',
      };
      this.dispatch(showNotification(notificationPayload));
      throw new Error('Error while fetching travel and activity mins!');
    }
  }

  async saveApprovedPlan(payload: {
    schedule: PlanDataType[];
    metrics: {
      travel_time: DispatchPlanTravelTimeMetricsResponseType[];
      fuel_usage: DispatchPlanFuelUsageMetricsResponseType[];
      material_dumped: DispatchPlanMaterialDumpResponseType[];
      trips: DispatchPlanTripsResponseType[];
    };
    mlInputPayload: Record<string, any>;
    startDateTime: string;
    approvedBy: string;
    status: DispatchPlanStatusType;
    planName: string;
    endDateTime: string;
    startOfDay: string;
    totalTonnage: number;
  }): Promise<string> {
    try {
      const serializedPayload = {
        ...payload,
        schedule: payload.schedule.map(serializePlanRow),
      };
      const response = await this.api.saveApprovedPlan(serializedPayload);
      // const notificationPayload: NotificationState = {
      //   isLoading: false,
      //   message: 'Plan approved!',
      //   show: true,
      //   type: 'success',
      // };
      // this.dispatch(showNotification(notificationPayload));
      return response;
    } catch (_) {
      this.handleError('Error while saving!');
      const notificationPayload: NotificationState = {
        isLoading: false,
        message: 'Error while saving!',
        show: true,
        type: 'error',
      };
      this.dispatch(showNotification(notificationPayload));
      throw new Error('Error while saving!');
    }
  }

  async getDispatchPlanSummaryByPlanId(
    planDate: string,
    updateRedux: boolean,
    planType: DispatchPlanSummaryPlanType,
    planId: string,
    planName: string
  ) {
    try {
      const response = await this.api.getApprovedDispatchPlanSummaryById({
        planDate,
        planId,
      });
      const transformedResponse = {
        planData: response?.planData?.schedule || [],
        metrics: response?.planData?.metrics || {},
        planName: planName ?? response?.planName ?? '',
        inputData: response?.inputData ?? {},
        planType,
      };
      // if (updateRedux) {
      //   this.dispatch(setDispatchSummaryPlan(transformedResponse));
      // }
      return transformedResponse;
    } catch (error) {
      console.log(error);
      const msg = 'Error while fetching latest plan!';
      this.handleError(msg);

      throw new Error(msg);
    }
  }

  async getDispatchPlanSummaryByDay(
    dayStartDateTime: string,
    dayEndDateTime: string,
    planDate: string,
    updateRedux: boolean,
    planType: DispatchPlanSummaryPlanType
  ) {
    try {
      const response: DispatchPlanSummaryType =
        await this.api.getApprovedDispatchPlanSummaryByDay({
          dayStartDateTime,
          dayEndDateTime,
          planDate,
        });

      const updatedResponse: DispatchPlanSummaryType = {
        ...response,
        planType,
      };

      console.log({ updatedResponse, response });

      if (
        Array.isArray(updatedResponse.plans) &&
        updatedResponse.plans.length
      ) {
        if (updateRedux) this.dispatch(setDispatchSummaryPlan(updatedResponse));
        return updatedResponse;
      } else {
        throw new Error('Invalid Plan data');
      }
    } catch (error) {
      console.log(error);
      const msg = 'Error while fetching latest plan!';
      this.handleError(msg);
      if (updateRedux) {
        const temp: DispatchPlanSummaryType = {
          planName: '',
          planType: 'no_plan',
          plans: [],
        };
        if (updateRedux) this.dispatch(setDispatchSummaryPlan(temp));
        return temp;
      }
    }
  }

  async getDispatchPlanHistory(
    startDateWithTime?: string,
    endDateWithTime?: string
  ) {
    try {
      const response: DispatchPlanHistoryResponseItemType[] =
        await this.api.getDispatchPlanHistory({
          startDate: startDateWithTime,
          endDate: endDateWithTime,
        });
      if (Array.isArray(response)) {
        return response;
      } else {
        throw new Error('Invalid dispatch plan history data format');
      }
    } catch (error) {
      const msg = 'Error while fetching dispatch plan history!';
      this.handleError(msg);
      const notificationPayload: NotificationState = {
        isLoading: false,
        message: msg,
        show: true,
        type: 'error',
      };
      this.dispatch(showNotification(notificationPayload));
      throw new Error(msg);
    }
  }

  async setActiveApprovedDispatchPlan(
    historyId: string,
    approvedBy: string,
    startDateTime: string,
    endDateTime: string,
    startOfDay: string
  ) {
    try {
      const response: string = await this.api.setActiveApprovedDispatchPlan({
        historyId,
        approvedBy,
        startDateTime,
        endDateTime,
        startOfDay,
      });
      return response;
    } catch (error) {
      const msg = 'Error while setting active approved dispatch plan!';
      this.handleError(msg);
      const notificationPayload: NotificationState = {
        isLoading: false,
        message: msg,
        show: true,
        type: 'error',
      };
      this.dispatch(showNotification(notificationPayload));
      throw new Error(msg);
    }
  }

  async deleteDispatchPlanHistoryRecord(historyId: string) {
    try {
      const response: string = await this.api.deleteDispatchPlanHistoryRecord({
        historyId,
      });
      return response;
    } catch (error) {
      const msg = 'Error while deleting!';
      this.handleError(msg);
      const notificationPayload: NotificationState = {
        isLoading: false,
        message: msg,
        show: true,
        type: 'error',
      };
      this.dispatch(showNotification(notificationPayload));
      throw new Error(msg);
    }
  }

  async getSiteConfig(): Promise<SiteConfigPayload | null> {
    try {
      const response = await this.api.getSiteConfig();
      this.dispatch(loadSiteConfigFromAPI(response));
      return response;
    } catch (error: any) {
      if (error?.isAxiosError && error?.response?.status === 404) {
        return null;
      }
      const msg = 'Failed to load site configuration.';
      this.handleError(msg);
      this.dispatch(
        showNotification({
          isLoading: false,
          message: msg,
          show: true,
          type: 'error',
        })
      );
      throw new Error(msg);
    }
  }

  async upsertSiteConfig(payload: SiteConfigPayload): Promise<void> {
    try {
      await this.api.upsertSiteConfig(payload);
      this.dispatch(
        showNotification({
          isLoading: false,
          message: 'Site configuration saved!',
          show: true,
          type: 'success',
        })
      );
    } catch (error) {
      const msg = 'Failed to save site configuration.';
      this.handleError(msg);
      this.dispatch(
        showNotification({
          isLoading: false,
          message: msg,
          show: true,
          type: 'error',
        })
      );
      throw new Error(msg);
    }
  }

  async getMinimalFleetCount(payload: {
    customerId: string;
    grades: Array<{ gradeId: string; materialGrade: number }>;
    dumpingSites: Array<{ locationId: string; targetBlend: number }>;
    shiftNumber: number;
    targetDeliveredTons: number;
    targetBlendTolerance: number;
  }): Promise<void> {
    try {
      const data = await this.api.getMinimalFleetCount(payload);
      let count: number | null = null;
      if (data && typeof data === 'object') {
        const total = Object.values(data as Record<string, unknown>)
          .filter((v): v is number => typeof v === 'number')
          .reduce((sum, v) => sum + v, 0);
        count = total > 0 ? total : null;
      }
      this.dispatch(setMinimalFleetCount(count));
    } catch (err) {
      this.dispatch(setMinimalFleetCount(null));
      throw err;
    }
  }
}
